import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/** Key used to prevent infinite reload loops */
const RELOAD_KEY = 'sime-chunk-reload';

function isChunkLoadError(error: Error): boolean {
  const msg = error.message || '';
  return (
    error.name === 'ChunkLoadError' ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('text/html')
  );
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      '[ErrorBoundary] Unhandled rendering error:',
      error,
      info.componentStack,
    );

    // Auto-reload once on stale chunk errors (after deploy)
    if (isChunkLoadError(error)) {
      const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);
      if (!alreadyReloaded) {
        sessionStorage.setItem(RELOAD_KEY, '1');
        window.location.reload();
        return;
      }
      // Clear flag so next navigation can try again
      sessionStorage.removeItem(RELOAD_KEY);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-sime-bg px-4">
          <div className="text-center p-4 md:p-8 max-w-md w-full">
            <h1 className="text-xl md:text-2xl font-bold text-sime-text-primary mb-3 md:mb-4">
              Ocurrio un error inesperado
            </h1>
            <p className="text-sm md:text-base text-sime-text-secondary mb-5 md:mb-6">
              Por favor recargue la pagina. Si el problema persiste, contacte a
              soporte.
            </p>
            <button
              onClick={() => { window.location.reload(); }}
              className="w-full sm:w-auto px-6 py-3 min-h-[44px] rounded-xl bg-sime-accent text-white font-medium"
            >
              Recargar pagina
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
